import React from 'react';
import { motion } from 'motion/react';
import { ArrowLeft, Mail, Phone, MapPin, Heart, Globe, Zap, Headphones, Shield, Target, Users, Star, Wifi } from 'lucide-react';

const sectionVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 220, damping: 22 } },
};

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.06, delayChildren: 0.1 } },
};

function ValueCard({ icon: Icon, title, desc }: { icon: any; title: string; desc: string }) {
  return (
    <div className="rounded-2xl p-4" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
      <div className="w-9 h-9 rounded-xl flex items-center justify-center mb-2.5" style={{ background: 'rgba(16,185,129,0.12)' }}>
        <Icon size={17} className="text-emerald-400" />
      </div>
      <p className="text-white font-bold text-sm mb-1">{title}</p>
      <p className="text-white/40 text-[12px] leading-relaxed">{desc}</p>
    </div>
  );
}

export default function AboutUs() {
  return (
    <div
      className="min-h-screen w-full font-sans flex flex-col"
      style={{ background: 'radial-gradient(ellipse at 60% 0%, #0a1f1a 0%, #040d09 50%, #020807 100%)' }}
    >
      {/* Ambient orbs */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden -z-10">
        <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full opacity-15" style={{ background: 'radial-gradient(circle, #059669 0%, transparent 70%)', filter: 'blur(60px)' }} />
        <div className="absolute top-1/3 -left-48 w-[500px] h-[500px] rounded-full opacity-10" style={{ background: 'radial-gradient(circle, #059669 0%, transparent 60%)', filter: 'blur(80px)' }} />
        <div className="absolute bottom-0 left-1/4 w-80 h-80 rounded-full opacity-10" style={{ background: 'radial-gradient(circle, #f59e0b 0%, transparent 70%)', filter: 'blur(50px)' }} />
      </div>

      {/* Sticky Header */}
      <div className="sticky top-0 z-30 backdrop-blur-xl border-b border-white/5 px-4 py-3 flex items-center shrink-0" style={{ background: 'rgba(4, 13, 9, 0.85)' }}>
        <motion.button
          whileTap={{ scale: 0.94 }}
          onClick={() => window.history.back()}
          className="flex items-center gap-1.5 text-white/60 hover:text-white transition-colors text-xs font-bold"
        >
          <ArrowLeft size={15} />
          Back to Home
        </motion.button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto pb-16">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="w-full max-w-3xl mx-auto px-5 sm:px-6 pt-8 pb-8"
        >
          {/* Hero */}
          <motion.div variants={sectionVariants} className="mb-10 text-center">
            <div className="flex justify-center mb-5">
              <div className="relative">
                <div className="absolute inset-0 rounded-3xl blur-xl" style={{ background: 'rgba(16,185,129,0.3)' }} />
                <div className="relative w-16 h-16 rounded-3xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #064e3b, #059669)', border: '1px solid rgba(16,185,129,0.4)' }}>
                  <span className="text-2xl font-black text-white">K</span>
                </div>
              </div>
            </div>
            <h1 className="text-3xl sm:text-4xl font-black text-white tracking-tight mb-2">
              About{' '}
              <span className="bg-gradient-to-r from-emerald-400 via-teal-400 to-cyan-400 bg-clip-text text-transparent">
                Kangavena
              </span>
            </h1>
            <p className="text-sm text-white/45 max-w-md mx-auto leading-relaxed">
              Empowering Sri Lankan businesses with smart, affordable point-of-sale technology.
            </p>
          </motion.div>

          {/* Mission */}
          <motion.div variants={sectionVariants} className="mb-10">
            <div className="rounded-3xl overflow-hidden" style={{ background: 'linear-gradient(135deg, #064e3b 0%, #065f46 40%, #047857 60%, #059669 100%)' }}>
              <div className="absolute inset-0 pointer-events-none" style={{ boxShadow: 'inset 0 0 40px rgba(16,185,129,0.15)' }} />
              <div className="relative z-10 p-6 sm:p-8">
                <div className="flex items-center gap-2 mb-3">
                  <Target size={18} className="text-emerald-200" />
                  <span className="text-[10px] font-black text-emerald-200/70 uppercase tracking-[0.2em]">Our Mission</span>
                </div>
                <p className="text-white text-lg sm:text-xl font-bold leading-snug">
                  To empower every Sri Lankan business with smart, affordable, and reliable point-of-sale technology — so they can focus on what they do best: serving their customers and growing their dream.
                </p>
              </div>
            </div>
          </motion.div>

          {/* What We Do */}
          <motion.div variants={sectionVariants} className="mb-10">
            <p className="text-sm font-black text-white/80 tracking-tight mb-4">What We Do</p>
            <div className="rounded-2xl p-5 space-y-4" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: 'rgba(16,185,129,0.12)', border: '1px solid rgba(16,185,129,0.2)' }}>
                  <Zap size={15} className="text-emerald-400" />
                </div>
                <div>
                  <p className="text-white font-bold text-sm">Cloud POS for Every Business</p>
                  <p className="text-white/45 text-[12px] leading-relaxed mt-0.5">
                    From corner shops to multi-branch retail chains, hotels to auto garages — we build the smartest POS system designed specifically for Sri Lankan businesses. Voice billing in Sinhala, offline-first reliability, and real-time analytics at your fingertips.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.2)' }}>
                  <Star size={15} className="text-amber-400" />
                </div>
                <div>
                  <p className="text-white font-bold text-sm">Luna AI Assistant</p>
                  <p className="text-white/45 text-[12px] leading-relaxed mt-0.5">
                    Luna is our AI-powered business assistant that handles voice billing, inventory queries, and analytics — in Sinhala and English. She works 24/7 so your business never stops, even when you do.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: 'rgba(59,130,246,0.12)', border: '1px solid rgba(59,130,246,0.2)' }}>
                  <Globe size={15} className="text-blue-400" />
                </div>
                <div>
                  <p className="text-white font-bold text-sm">Multi-Store Management & Real-Time Analytics</p>
                  <p className="text-white/45 text-[12px] leading-relaxed mt-0.5">
                    Run one store or twenty — our platform syncs inventory, sales, and analytics in real-time across all locations. Advanced and lifetime analytics give you the insights to make smarter business decisions.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Our Values */}
          <motion.div variants={sectionVariants} className="mb-10">
            <p className="text-sm font-black text-white/80 tracking-tight mb-4">Our Values</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <ValueCard icon={Heart} title="Simplicity" desc="Powerful doesn't have to be complicated. Every feature is designed to be intuitive." />
              <ValueCard icon={Globe} title="Affordability" desc="Enterprise-grade POS technology at prices that make sense for Sri Lankan businesses." />
              <ValueCard icon={Wifi} title="24/7 Reliability" desc="99.9% uptime with offline-first architecture. Your business never stops." />
              <ValueCard icon={Headphones} title="Local Support" desc="Real support, in Sinhala and English, from a team that understands your market." />
            </div>
          </motion.div>

          {/* Stats */}
          <motion.div variants={sectionVariants} className="mb-10">
            <p className="text-sm font-black text-white/80 tracking-tight mb-4">Kangavena by the Numbers</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <div className="rounded-2xl p-5 text-center" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)' }}>
                <p className="text-2xl font-black text-emerald-400">100+</p>
                <p className="text-[11px] text-white/40 mt-1 font-medium">Businesses Trust Us</p>
              </div>
              <div className="rounded-2xl p-5 text-center" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)' }}>
                <p className="text-2xl font-black text-emerald-400">99.9%</p>
                <p className="text-[11px] text-white/40 mt-1 font-medium">Platform Uptime</p>
              </div>
              <div className="rounded-2xl p-5 text-center" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)' }}>
                <p className="text-2xl font-black text-emerald-400">24/7</p>
                <p className="text-[11px] text-white/40 mt-1 font-medium">AI Support</p>
              </div>
            </div>
          </motion.div>

          {/* Team / Vision */}
          <motion.div variants={sectionVariants} className="mb-10">
            <p className="text-sm font-black text-white/80 tracking-tight mb-4">Our Engineering & Vision</p>
            <div className="rounded-2xl p-5" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 rounded-2xl flex items-center justify-center shrink-0 text-lg font-black text-white" style={{ background: 'linear-gradient(135deg, #064e3b, #059669)', border: '1px solid rgba(16,185,129,0.3)' }}>
                  KC
                </div>
                <div>
                  <p className="text-white font-bold text-base">Kangavena Engineering</p>
                  <p className="text-emerald-400/70 text-xs font-medium mb-2">AI Architecture & Development · Sri Lanka</p>
                  <p className="text-white/45 text-[12px] leading-relaxed">
                    Kangavena Cloud was built from a simple idea: Sri Lankan businesses deserve world-class POS and AI technology without the world-class price tag. Based in Sri Lanka, the Kangavena team is committed to building tools that truly serve the local market — from Sinhala voice billing to offline-first reliability.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Contact */}
          <motion.div variants={sectionVariants} className="mb-10">
            <p className="text-sm font-black text-white/80 tracking-tight mb-4">Get in Touch</p>
            <div className="rounded-2xl p-5" style={{ background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.15)' }}>
              <p className="text-white/55 text-[13px] mb-3">We'd love to hear from you. Whether you want to register your business, have a question, or just want to say hello — reach out anytime.</p>
              <div className="space-y-3">
                <a href="https://wa.me/94767114888" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2.5 text-white/60 hover:text-emerald-400 transition-colors">
                  <Phone size={14} className="text-emerald-500" />
                  <span className="text-sm">WhatsApp: 076 711 4888</span>
                </a>
                <a href="mailto:sissara.com@gmail.com" className="flex items-center gap-2.5 text-white/60 hover:text-emerald-400 transition-colors">
                  <Mail size={14} className="text-emerald-500" />
                  <span className="text-sm">sissara.com@gmail.com</span>
                </a>
                <div className="flex items-center gap-2.5 text-white/60">
                  <MapPin size={14} className="text-emerald-500" />
                  <span className="text-sm">Sri Lanka</span>
                </div>
              </div>
              <div className="mt-4">
                <a
                  href="https://wa.me/94767114888?text=Hi%20Kangavena%2C%20I%20want%20to%20register%20my%20store"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold text-white transition-all hover:scale-[1.03] active:scale-95"
                  style={{ background: 'linear-gradient(135deg, #25D366, #128C7E)', boxShadow: '0 4px 20px rgba(37,211,102,0.35)' }}
                >
                  <Shield size={15} />
                  Register Your Business
                </a>
              </div>
            </div>
          </motion.div>

          {/* Back to Home */}
          <motion.div variants={sectionVariants} className="text-center pt-4 pb-8">
            <a href="/" className="inline-flex items-center gap-2 text-xs font-bold text-emerald-400/60 hover:text-emerald-400 transition-colors">
              <ArrowLeft size={13} />
              Back to Home
            </a>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
}
